import React, { useEffect, useState, useRef } from 'react';
import { Container } from 'react-bootstrap';
import { Link, useSearchParams, useLocation } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faCheckCircle, 
  faTruck, 
  faBox, 
  faHome, 
  faEnvelope, 
  faBarcode, 
  faList, 
  faShoppingCart, 
  faArrowRight,
  faMoneyBillWave,
  faInfoCircle,
  faExclamationTriangle
} from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import api from '../config/api';
import './CashOnDeliverySuccess.css';

const CashOnDeliverySuccess = () => {
  const { user } = useAuth();
  const { clearCart } = useCart();
  const [searchParams] = useSearchParams();
  const orderNumber = searchParams.get('orderId');
  const [orderDetails, setOrderDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const cartCleared = useRef(false);
  
  // Location state'den kapıda nakit ödeme bilgilerini al
  const location = useLocation();
  const { 
    paymentMethod, 
    deliveryId, 
    trackingNumber, 
    isGuest,
    orderId: stateOrderId 
  } = location.state || {};

  // URL'den veya state'den order ID'yi al
  const finalOrderId = orderNumber || stateOrderId;

  useEffect(() => {
    const processOrder = async () => {
      if (finalOrderId && !cartCleared.current) {
        try {
          console.log('🔄 Kapıda ödeme başarılı, sipariş detayları alınıyor...');
          
          // Sipariş detaylarını getir
          const orderData = await api.get(`/orders/details/${finalOrderId}`);
          setOrderDetails(orderData.data);
          console.log('✅ Sipariş detayları alındı:', orderData.data);

          // Sepeti temizle
          cartCleared.current = true;
          await clearCart();
          console.log('✅ Sepet temizlendi');
          
        } catch (error) {
          console.error('❌ Sipariş detayları alınamadı:', error);
          setError('Sipariş detayları alınamadı. Lütfen müşteri hizmetleri ile iletişime geçin.');
        } finally {
          setLoading(false);
        }
      } else if (!finalOrderId) {
        console.error('❌ Order ID bulunamadı');
        setError('Sipariş bilgisi bulunamadı. Lütfen müşteri hizmetleri ile iletişime geçin.');
        setLoading(false);
      }
    };

    processOrder();
  }, [finalOrderId, clearCart]);

  const orderDate = new Date().toLocaleDateString('tr-TR', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });

  // Tahmini teslimat tarihi (3 iş günü sonrası)
  const deliveryDate = new Date();
  deliveryDate.setDate(deliveryDate.getDate() + 3);
  const estimatedDelivery = deliveryDate.toLocaleDateString('tr-TR', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });

  if (loading) {
    return (
      <div className="cod-success-page">
        <Container>
          <div className="loading-content">
            <div className="loading-spinner">
              <FontAwesomeIcon icon={faTruck} className="spinning" />
            </div>
            <h2>Siparişiniz İşleniyor...</h2>
            <p>Lütfen bekleyin, sipariş bilgileriniz hazırlanıyor.</p>
          </div>
        </Container>
      </div>
    );
  }

  if (error) {
    return (
      <div className="cod-success-page">
        <Container>
          <div className="error-content">
            <div className="error-icon">
              <FontAwesomeIcon icon={faExclamationTriangle} />
            </div>
            <h2>Bir Hata Oluştu</h2>
            <p className="error-message">{error}</p>
            <div className="error-actions">
              <Link to="/contact" className="btn btn-primary">
                <FontAwesomeIcon icon={faEnvelope} /> Müşteri Hizmetleri
              </Link>
              <Link to="/" className="btn btn-outline-secondary">
                <FontAwesomeIcon icon={faHome} /> Ana Sayfa
              </Link>
            </div>
          </div>
        </Container>
      </div>
    );
  }

  return (
    <div className="cod-success-page">
      <Container>
        <div className="success-content">
          {/* Başarı İkonu */}
          <div className="success-icon">
            <FontAwesomeIcon icon={faCheckCircle} />
          </div>
          
          {/* Ana Başlık */}
          <h1>Kapıda Ödeme Siparişiniz Başarıyla Oluşturuldu!</h1>
          
          {/* Sipariş Bilgileri */}
          <div className="order-info">
            <h3><FontAwesomeIcon icon={faBox} /> Sipariş Bilgileri</h3>
            <div className="info-grid">
              <div className="info-item">
                <span className="label">Sipariş Numarası:</span>
                <span className="value">{orderDetails?.order_number || finalOrderId}</span>
              </div>
              <div className="info-item">
                <span className="label">Sipariş Tarihi:</span>
                <span className="value">{orderDate}</span>
              </div>
              <div className="info-item">
                <span className="label">Tahmini Teslimat:</span>
                <span className="value">{estimatedDelivery}</span>
              </div>
            </div>
          </div>

          {/* Kapıda Ödeme Bilgileri */}
          <div className="cod-info">
            <h3>
              <FontAwesomeIcon icon={faMoneyBillWave} /> 
              Kapıda Ödeme Detayları
            </h3>
            <div className="cod-highlight">
              <FontAwesomeIcon icon={faInfoCircle} />
              <span>Siparişiniz teslimat sırasında nakit olarak ödenecektir.</span>
            </div>
            
            <div className="info-grid">
              <div className="info-item">
                <span className="label">Ödeme Yöntemi:</span>
                <span className="value">Kapıda Nakit Ödeme</span>
              </div>
              {trackingNumber && (
                <div className="info-item">
                  <span className="label">Kargo Takip Numarası:</span>
                  <a 
                    href={`https://hepsijet.com/gonderi-takibi/${trackingNumber}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="value tracking-number tracking-link"
                  >
                    {trackingNumber}
                    <FontAwesomeIcon icon={faArrowRight} className="ms-2" />
                  </a>
                </div>
              )}
            </div>
          </div>

          {/* Kargo Bilgileri */}
          <div className="shipping-info">
            <h3><FontAwesomeIcon icon={faTruck} /> Kargo Bilgileri</h3>
            <div className="info-grid">
              <div className="info-item">
                <span className="label">Kargo Firması:</span>
                <span className="value">Hepsijet</span>
              </div>
              <div className="info-item">
                <span className="label">Teslimat Süresi:</span>
                <span className="value">1-3 iş günü</span>
              </div>
            </div>
            
            {trackingNumber && (
              <div className="tracking-section">
                <p className="tracking-info">
                  <FontAwesomeIcon icon={faBarcode} />
                  <strong>Kargo Takip:</strong> Siparişinizi takip etmek için kargo firmasının web sitesini kullanabilirsiniz.
                </p>
              </div>
            )}
          </div>

          {/* Teslimat Bilgileri */}
          {orderDetails && (
            <div className="customer-info">
              <h3><FontAwesomeIcon icon={faHome} /> Teslimat Adresi</h3>
              <div className="address-details">
                <p className="customer-name">
                  {orderDetails.first_name} {orderDetails.last_name}
                </p>
                <p className="address">{orderDetails.address}</p>
                <p className="location">
                  {orderDetails.district} / {orderDetails.city}
                </p>
                <p className="email">
                  <FontAwesomeIcon icon={faEnvelope} /> {orderDetails.email}
                </p>
              </div>
            </div>
          )}

          {/* Misafir Kullanıcı Uyarısı */}
          {isGuest && (
            <div className="guest-warning">
              <div className="warning-icon">
                <FontAwesomeIcon icon={faExclamationTriangle} />
              </div>
              <div className="warning-content">
                <h4>Misafir Kullanıcı</h4>
                <p>
                  Sipariş bilgileriniz ve kargo takip numaranız e-posta adresinize gönderilecektir. 
                  Lütfen e-posta kutunuzu kontrol ediniz.
                </p>
              </div>
            </div>
          )}

          {/* Sonraki Adımlar */}
          <div className="next-steps">
            <h3>Sonraki Adımlar</h3>
            <div className="steps-list">
              <div className="step">
                <div className="step-number">1</div>
                <div className="step-content">
                  <strong>Siparişiniz hazırlanıyor</strong>
                  <p>Ürünleriniz paketlenip kargoya verilecek</p>
                </div>
              </div>
              <div className="step">
                <div className="step-number">2</div>
                <div className="step-content">
                  <strong>Kargo yola çıkıyor</strong>
                  <p>Kargo firması ürünlerinizi adresinize getirecek</p>
                </div>
              </div>
              <div className="step">
                <div className="step-number">3</div>
                <div className="step-content">
                  <strong>Teslimat ve ödeme</strong>
                  <p>Ürünler teslim edilirken nakit ödeme alınacak</p>
                </div>
              </div>
            </div>
          </div>

          {/* Aksiyon Butonları */}
          <div className="action-buttons">
            {user && (
              <Link to="/orders" className="btn btn-outline-primary">
                <FontAwesomeIcon icon={faList} /> Siparişlerim
              </Link>
            )}
            <Link to="/" className="btn btn-primary">
              <FontAwesomeIcon icon={faShoppingCart} /> Alışverişe Devam Et
            </Link>
          </div>

          {/* Bilgi Notu */}
          <div className="info-note">
            <p>
              <FontAwesomeIcon icon={faInfoCircle} />
              <strong>Önemli:</strong> Kapıda ödeme siparişlerinizde, ürünler teslim edilirken 
              nakit ödeme alınmaktadır. Kargo firması ürünleri teslim etmeden ödeme almayacaktır.
            </p>
          </div>
        </div>
      </Container>
    </div>
  );
};

export default CashOnDeliverySuccess;
